import React from 'react';
import clsx from 'clsx';
import millify from 'millify';
import { gql } from '@soundxyz/gql-string';
import type { CollectButtonSource } from '@soundxyz/utils/analytics';
import { ERROR_TYPE, MINTING_ERROR_ACTIONS, PILLARS } from '@soundxyz/utils/constants/logging';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { CollectButtonV3 } from '@components/CollectButton/CollectButtonV3';
import { PanelWrapper } from '@components/Song/MintComponents/MintComponent/PanelComponents/PanelWrapper';
import { PresaleBadge } from '@components/Song/MintComponents/MintComponent/PanelComponents/PresaleBadge';
import { NetworkIndicator } from '@components/Song/MintComponents/NetworkIndicator';
import { Tooltip } from '@components/main-components/tooltip/Tooltip';
import { BuyArtistContractModal } from '@components/modals/Buy';
import { CheckoutModal } from '@components/modals/Buy/Checkout/CheckoutModal';
import {
  getCollectionTypeForMarketplace,
  MarketPlaceCheckoutModal,
} from '@components/modals/Buy/MarketPlaceCheckout';
import { useAuth } from '@context/auth/base';
import { ModalContext, ModalType } from '@context/modal';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment, RegularMintInfoFragmentDoc } from '@graphql/reactQuery';
import { useLogError } from '@hooks/useLogErrorNew';
import { useMarketPlace } from '@hooks/useMarketPlace';
import { getSingularOrPlural } from '@utils/helpers';
import type { PlaylistReferral } from '@utils/types';
import { getMarketPlaceUrl } from '@utils/url';
import { useMintComponent } from './MintComponentContext';
import { MintComponentPanelBadge } from './PanelComponents/PanelBadge';
import { MintComponentPanelSubInfo } from './PanelComponents/PanelSubInfo';
import { SocialProofBar } from './SocialProof';
import { SongInfo } from './SongInfo';
import { MINT_COMPONENT_HEADING_SUBTEXT } from './constants';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment RegularMintInfo on Release {
    id
    type
    contractAddress
    title
    chainId
    finalQuantity
    price
    marketPlaceUrl
    samAddress
    contract {
      id
      contractType
    }
    ...BondingCurveMintData
    ...CheckoutReleaseInfo
    ...MintComponentSongInfo
    ...MarketplaceCheckout
  }
`);

//TODO: Add tests : https://linear.app/sound/issue/ENG-7405/add-the-rest-of-tests-for-mint-component
export const RegularMintComponent = ({
  releaseInfo,
  collectButtonSource,
  isInModal,
  playlistReferral,
}: {
  releaseInfo: FragmentType<RegularMintInfoFragmentDoc>;
  collectButtonSource: CollectButtonSource;
  isInModal?: boolean;
  playlistReferral: PlaylistReferral | null;
}) => {
  const { isAuthenticatedWithAPI } = useAuth();
  const logError = useLogError();
  const release = getFragment(RegularMintInfoFragmentDoc, releaseInfo);
  const isSamEnabled = !!release.samAddress;
  const {
    id: releaseId,
    chainId,
    finalQuantity,
    contractAddress,
    contract: { contractType },
    type: releaseType,
    title: releaseTitle,
  } = release;
  const { openModal } = ModalContext.useContainer();
  const {
    presaleDetails,
    publicSaleDetails,
    hasSaleAccess,
    price,
    isOpenEdition,
    isFixedEdition,
    showDisabledCollect,
    loading: isMintComponentLoading,
    totalMinted,
    showPresaleBadge,
  } = useMintComponent();

  const collectionType = getCollectionTypeForMarketplace({ contractType, releaseType });
  const { isLoading: isMarketPlaceLoading, marketPlaceFloorPriceInEth } = useMarketPlace({
    releaseId,
    contractAddress: contractAddress,
    contractChainId: chainId,
    quantity: 0,
    releaseTitle,
    collectionType,
  });

  const isLoading = isMintComponentLoading || isMarketPlaceLoading;

  const maxSupply = finalQuantity ?? publicSaleDetails.maxMintableLower;

  // Marketplace button related logic
  const marketPlaceUrl = getMarketPlaceUrl(release.marketPlaceUrl, contractAddress, chainId);

  const userMaxedOut = publicSaleDetails.saleStarted && !hasSaleAccess && isAuthenticatedWithAPI;
  const isPublicSaleOverOrUserHitLimit = publicSaleDetails.isSoldOut || userMaxedOut;
  const showPurchaseSecondaryListings =
    isPublicSaleOverOrUserHitLimit && !!marketPlaceFloorPriceInEth;

  const priceDisplayedToUser = showPurchaseSecondaryListings ? marketPlaceFloorPriceInEth : price;

  const onBuy = useStableCallback(() => {
    switch (contractType) {
      case 'ARTIST':
        return openModal(ModalType.BUY_NFT, {
          body: showPurchaseSecondaryListings ? (
            <MarketPlaceCheckoutModal
              releaseData={release}
              contractAddress={contractAddress}
              marketPlacePlaceUrl={marketPlaceUrl}
            />
          ) : (
            <BuyArtistContractModal releaseId={releaseId} />
          ),
        });
      case 'EDITION':
        return openModal(ModalType.RELEASE_CHECKOUT, {
          body: showPurchaseSecondaryListings ? (
            <MarketPlaceCheckoutModal
              releaseData={release}
              contractAddress={contractAddress}
              marketPlacePlaceUrl={marketPlaceUrl}
            />
          ) : (
            <CheckoutModal
              releaseData={release}
              totalMinted={totalMinted}
              playlistReferral={playlistReferral}
            />
          ),
        });
      default:
        logError({
          action: MINTING_ERROR_ACTIONS.EDITION_MINT_ERROR,
          error: new Error(`Contract type ${contractType} not supported`),
          toast: 'Error while collecting edition C',
          message: `Errored while collecting, contract type ${contractType} not supported`,
          errorType: ERROR_TYPE.EDITION_MINT,
          indexedTags: {
            releaseId,
            chainId,
          },
          isArtistOnlyPage: false,
          pillar: PILLARS.MINTING,
          level: 'fatal',
        });
    }
  });

  if (isLoading && !isInModal) {
    return <div className="h-52 w-full animate-pulse rounded-lg bg-neutral200 lt:max-w-sm" />;
  }

  const { heading, subText } = getMintInfoText({
    isOpenEdition,
    isFixedEdition,
    isSamEnabled,
    soundMarket: isPublicSaleOverOrUserHitLimit,
  });

  return (
    <div className="flex w-full flex-col rounded-md lt:max-w-sm">
      <div
        className={clsx(
          'w-full overflow-hidden rounded-t-lg bg-white',
          isInModal ? 'flex border-b border-base200 pb-4' : 'hidden p-4 shadow-tertiary lt:flex',
        )}
      >
        <SongInfo release={release} />
      </div>
      <div
        className={clsx(
          'z-above1 flex w-full flex-col gap-4 bg-white',
          isInModal ? 'pt-4' : 'rounded-t-lg p-4 shadow-tertiary lt:rounded-t-none',
        )}
      >
        {isLoading && isInModal ? (
          <div className="h-[106px] w-full animate-pulse rounded-lg bg-neutral200" />
        ) : (
          <PanelWrapper selected subInfo={<MintComponentPanelSubInfo />}>
            <div className="flex flex-col gap-3 p-3">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <h2 className="font-title text-title-m font-medium text-base900">{heading}</h2>
                  {showPresaleBadge && !!presaleDetails && (
                    <PresaleBadge
                      releaseId={release.id}
                      hasPresaleAccess={presaleDetails.hasAccess}
                      merkleRoot={presaleDetails.merkleRoot}
                    />
                  )}
                </div>
                <p className="font-base text-base-s text-base500">{subText}</p>
              </div>
              <div className="flex w-full items-center justify-between">
                <div className="inline-flex items-center font-base text-base-s font-semibold">
                  <span className="flex items-center gap-1">
                    <NetworkIndicator chainId={chainId} />
                    <Tooltip openOnClick={false}>
                      <Tooltip.Trigger
                        className={clsx(
                          'font-base text-base-s font-semibold',
                          !!presaleDetails?.hasAccess && showPresaleBadge && 'text-fuchsia500',
                        )}
                      >
                        {priceDisplayedToUser}
                      </Tooltip.Trigger>
                      <Tooltip.StyledContent className="text-center font-base text-base-s">
                        {!!presaleDetails && <p>Presale Price: {presaleDetails.formattedPrice}</p>}
                        <p>Public sale price: {publicSaleDetails.formattedPrice}</p>
                      </Tooltip.StyledContent>
                    </Tooltip>
                  </span>
                  &nbsp;
                  <span className="text-base400">•</span>
                  &nbsp;
                  <span>
                    {isOpenEdition ? (
                      <>
                        {millify(totalMinted)}&nbsp;
                        {getSingularOrPlural({
                          count: totalMinted,
                          word: 'mint',
                        })}
                      </>
                    ) : (
                      <>
                        {totalMinted}/{maxSupply} minted
                      </>
                    )}
                  </span>
                </div>
                <MintComponentPanelBadge
                  showMarketPlaceBadge={showPurchaseSecondaryListings}
                  marketPlaceUrl={marketPlaceUrl}
                />
              </div>
            </div>
          </PanelWrapper>
        )}
        {isLoading && isInModal ? (
          <div className="h-11 w-full animate-pulse rounded-lg bg-neutral200" />
        ) : (
          <CollectButtonV3
            fullWidth
            sourceComponent={collectButtonSource}
            releaseId={release.id}
            marketPlaceUrl={marketPlaceUrl}
            isSaleOverOrUserHitLimit={isPublicSaleOverOrUserHitLimit}
            showDisabledCollect={showDisabledCollect}
            onBuy={onBuy}
            showPurchaseSecondaryListings={showPurchaseSecondaryListings}
          />
        )}
      </div>

      {!isInModal && <SocialProofBar releaseId={release.id} totalMinted={totalMinted} />}
    </div>
  );
};

const getMintInfoText = ({
  isOpenEdition,
  isFixedEdition,
  isSamEnabled,
  soundMarket,
}: {
  isOpenEdition: boolean;
  isFixedEdition: boolean;
  isSamEnabled: boolean;
  soundMarket: boolean;
}) => {
  if (soundMarket) {
    return MINT_COMPONENT_HEADING_SUBTEXT.MARKET;
  }

  /**
   * ordering here is important
   * isFixedEdition check handles expired Sound Swap that turned into Fixed
   * isOpenEdition can be true for both OE and SS so it should be checked last
   */
  if (isFixedEdition) {
    return MINT_COMPONENT_HEADING_SUBTEXT.FIXED;
  }

  if (isSamEnabled) return MINT_COMPONENT_HEADING_SUBTEXT.SAM;

  if (isOpenEdition) {
    return MINT_COMPONENT_HEADING_SUBTEXT.OPEN;
  }

  return MINT_COMPONENT_HEADING_SUBTEXT.FALLBACK;
};
